import React, { useEffect, useState } from "react";
import Select from "react-select";
import PropTypes from "prop-types";
import {
	Button,
	Col,
	Form,
	FormFeedback,
	FormGroup,
	Input,
	Label,
	Row,
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
} from "reactstrap";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import ataService from "services/AtaService";
import Swal from "sweetalert2";
import { registerLocale } from "react-datepicker";
import pt from "date-fns/locale/pt-BR";
import userService from "services/UserService";
import Loading from "components/Common/Loading";
import { FloppyDisk, X } from "@phosphor-icons/react";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";
registerLocale("pt-BR", pt);

const ModalCreateAta = ({ isOpen, toggle }) => {
	const customStyles = {
		option: (provided, state) => ({
			...provided,
			color: state.isSelected ? "blue" : "black",
			backgroundColor: "white",
			"&:hover": {
				backgroundColor: "#556ee6",
			},
		}),
	};

	const [usuarios, setUsuarios] = useState([]);
	const [selectedOptions, setSelectedOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();

	const validation = useFormik({
		initialValues: {
			name: "",
			description: "",
			collaborators: [],
			version: 1,
			consultants_only: false,
		},

		validate: (values) => {
			const errors = {};

			if (!values.name) {
				errors.name = "Campo obrigatório";
			}
			if (!values.description) {
				errors.description = "Campo obrigatório";
			}

			return errors;
		},

		onSubmit: async (values, { setSubmitting }) => {
			const errors = await validation.validateForm(values);
			if (Object.keys(errors).length === 0) {
				try {
					setLoading(true);
					const response = await ataService.post(values);
					SendaCustomSwal.fire({
						iconHtml: '<i class="success-icon"></i>',
						title: "Sucesso",
						text: "Formulário enviado com sucesso!",
						customClass: {
							confirmButton: "custom-confirm-button",
						},
					}).then(() => {
						setLoading(false);
						navigate(`../form-atas/${response.id}`);
					});
				} catch (error) {
					SendaCustomSwal.fire({
						iconHtml: '<i class="error-icon"></i>',
						title: "Oops...",
						text: "Algo deu errado!",
						customClass: {
							confirmButton: "custom-confirm-button",
						},
					});
				} finally {
					setLoading(false);
				}
			}
			setSubmitting(false);
		},
	});

	const getUsuarios = async () => {
		try {
			const response = await userService.get();
			const funcoes = response.map((item) => {
				return {
					value: item.id,
					label: item.lastname,
				};
			});
			setUsuarios(funcoes);
		} catch (error) {
			console.log(error);
		}
	};

	useEffect(() => {
		getUsuarios();
	}, []);

	return (
		<Modal isOpen={isOpen} toggle={toggle}>
			<ModalHeader toggle={toggle}>Criar Nova Ata</ModalHeader>
			<ModalBody>
				<Loading isLoading={loading}>
					<Form
						className="needs-validation"
						onSubmit={(e) => {
							e.preventDefault();
							validation.handleSubmit();
							return false;
						}}
					>
						<Row>
							<Col md="12">
								<FormGroup className="mb-3">
									<Label htmlFor="validationCustom01">
										Nome da Ata
									</Label>
									<Input
										name="name"
										placeholder="Nome da Ata"
										type="text"
										className="form-control"
										id="validationCustom01"
										onChange={validation.handleChange}
										onBlur={validation.handleBlur}
										value={validation.values.name || ""}
										required={true}
										invalid={
											validation.touched.name &&
											validation.errors.name
												? true
												: false
										}
									/>
									{validation.touched.name &&
									validation.errors.name ? (
										<FormFeedback type="invalid">
											{validation.errors.name}
										</FormFeedback>
									) : null}
								</FormGroup>
							</Col>
						</Row>

						<Row>
							<Col md="12">
								<FormGroup className="mb-3">
									<Label htmlFor="validationCustom01">
										Descrição
									</Label>
									<Input
										style={{ height: "350px" }}
										name="description"
										placeholder="Descrição"
										type="textarea"
										className="form-control"
										id="validationCustom01"
										onChange={validation.handleChange}
										onBlur={validation.handleBlur}
										required={true}
										value={
											validation.values.description || ""
										}
										invalid={
											validation.touched.description &&
											validation.errors.description
												? true
												: false
										}
									/>
									{validation.touched.description &&
									validation.errors.description ? (
										<FormFeedback type="invalid">
											{validation.errors.description}
										</FormFeedback>
									) : null}
								</FormGroup>
							</Col>
						</Row>

						<Row>
							<Col md="12">
								<FormGroup className="mb-3">
									<Label htmlFor="validationCustom01">
										Quem pode ver
									</Label>
									<Select
										isMulti={true}
										styles={customStyles}
										placeholder="Selecione quem pode ver a ata"
										onChange={(selectedOption) => {
											setSelectedOptions(selectedOption);
											const selectedValue =
												selectedOption?.map(
													(selected) =>
														selected.value,
												);
											validation.setFieldValue(
												"collaborators",
												selectedValue,
											);
										}}
										options={usuarios}
										value={selectedOptions}
									/>
								</FormGroup>
							</Col>
						</Row>

						<Row>
							<Col md="12">
								<FormGroup className="mb-3">
									<Input
										type="checkbox"
										className="form-check-input"
										id="consultants_only"
										name="consultants_only"
										onChange={(event) => {
											const value = event.target.checked
												? 1
												: 0;
											validation.setFieldValue(
												"consultants_only",
												value,
											);
										}}
									/>
									<label
										className="form-check-label"
										htmlFor="consultants_only"
										style={{ marginLeft: 10 }}
									>
										Apenas consultores
									</label>
								</FormGroup>
							</Col>
						</Row>
					</Form>
				</Loading>
			</ModalBody>
			<ModalFooter>
				<Button
					style={{
						background: "none",
						padding: 10,
						cursor: "pointer",
						color: "black",
						marginLeft: 10,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: 2,
					}}
					onClick={validation.handleSubmit}
				>
					{loading ? (
						<span
							className="spinner-border spinner-border-sm mr-1"
							style={{ marginRight: 10 }}
						></span>
					) : null}
					<FloppyDisk size={16} /> Salvar
				</Button>
				<Button
					style={{
						background: "none",
						padding: 10,
						cursor: "pointer",
						color: "black",
						marginLeft: 10,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: 2,
					}}
					onClick={toggle}
				>
					<X size={16} /> Cancelar
				</Button>
			</ModalFooter>
		</Modal>
	);
};

ModalCreateAta.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	toggle: PropTypes.func.isRequired,
};

export default ModalCreateAta;
